import { Separator } from '@components/Separator'
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form.tsx'
import type { Input } from '@components/ui/input.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip.tsx'
import { useIsMobile } from '@hooks/use-mobile.ts'
import { renderLabel } from '@utils/render'
import { CircleHelp } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'

type InputWrapperProps = ComponentProps<typeof Input> & {
  label?: string | ReactNode
  labelAction?: ReactNode
  description?: string
  helpText?: string
  labelClassName?: string
  children: ReactNode
  hideErrorMessage?: boolean
  separatorAfterLabel?: boolean
}

export function InputWrapper({
  name,
  label,
  labelAction,
  description,
  helpText,
  labelClassName,
  children,
  hideErrorMessage,
  separatorAfterLabel,
  ...props
}: InputWrapperProps) {
  const isMobile = useIsMobile()
  const labelId = useId()
  const isInsideForm = Boolean(name)

  if (isInsideForm) {
    return (
      <FormItem className={`flex flex-col min-w-min ${label && !description ? 'gap-2' : 'gap-1'}`}>
        {label && (
          <FormLabel
            className={`group flex gap-1 flex-row text-sm font-medium items-center leading-none ${labelClassName ?? ''}`}
          >
            {renderLabel(label)}
            {labelAction}
            {helpText && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleHelp size={16} strokeWidth={2} className="shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right" align="center" hidden={isMobile}>
                  {helpText}
                </TooltipContent>
              </Tooltip>
            )}
          </FormLabel>
        )}
        {separatorAfterLabel && <Separator className="mt-1" />}
        {description && (
          <FormDescription className="text-sm mt-0 mb-1 leading-normal whitespace-nowrap">
            {description}
          </FormDescription>
        )}
        <FormControl {...props}>{children}</FormControl>
        {!hideErrorMessage && <FormMessage className="mt-0 font-medium text-sm leading-normal" />}
      </FormItem>
    )
  }
  return (
    <FormItem className={`flex flex-col min-w-min ${label && !description ? 'gap-2' : 'gap-1'}`}>
      {description && (
        <p className="text-sm text-muted-foreground mt-0 mb-1 whitespace-nowrap">{description}</p>
      )}

      {label ? (
        // biome-ignore lint/a11y/noLabelWithoutControl: children can be various controls, label is associated via context
        <label
          id={labelId}
          className={`group flex gap-1 flex-row text-sm font-medium items-center leading-none ${labelClassName ?? ''}`}
        >
          {renderLabel(label)}
          {labelAction}
          {helpText && (
            <Tooltip>
              <TooltipTrigger asChild>
                <CircleHelp size={16} strokeWidth={2} />
              </TooltipTrigger>
              <TooltipContent side="right" align="center" hidden={isMobile}>
                {helpText}
              </TooltipContent>
            </Tooltip>
          )}
          {children}
        </label>
      ) : (
        children
      )}
    </FormItem>
  )
}
